<template>
  <div class="w-[640px] opus-progress">
    <div v-if="!isVideo" class="main-product">
      <a-carousel class="banner" arrows :autoplay="true" :autoplaySpeed="5000">
        <template #prevArrow>
          <div class="custom-slick-arrow left">
            <iconpark-icon name="arrow-left"></iconpark-icon>
          </div>
        </template>
        <template #nextArrow>
          <div class="custom-slick-arrow right">
            <iconpark-icon name="arrow-right"></iconpark-icon>
          </div>
        </template>
        <span
          v-for="(item, index) in normalBanners"
          :key="index"
          class="banner"
        >
          <Resource
            :src="item.image_url"
            :lazy="false"
            :transform-resource="transformImage"
            :resource-option="{
              format: 'webp',
            }"
            class="w-full h-full"
          ></Resource>
        </span>
      </a-carousel>
    </div>
    <video
      v-else
      :src="data.video_url || data.url?.[0]"
      controls
      class="w-full h-[360px] rounded-[8px_0_0_0] object-center bg-back-3"
      autoplay
      :class="
        data.vertical || data.project_resolution === '1080x1920'
          ? 'object-contain'
          : 'object-cover'
      "
    ></video>
    <div class="p-[24px]">
      <div class="flex-y-center justify-between">
        <div class="text-font-1 text-14 font-bold">
          {{ data.name || data.title }}
        </div>
        <div class="flex-y-center gap-[8px]">
          <template v-if="data.status === EXECUTION_STATUS.ERROR">
            <a-popover>
              <template #content> {{ $t('qu-xiao-he-cheng') }} </template>
              <span
                class="icon-action"
                @click="() => onAction(OPUS_ACTION_TYPE.CANCEL)"
              >
                <iconpark-icon name="chahao" size="18"></iconpark-icon>
              </span>
            </a-popover>
            <a-popover>
              <template #content> {{ $t('zhong-xin-he-cheng') }} </template>
              <span
                class="icon-action"
                @click="() => onAction(OPUS_ACTION_TYPE.RETRY)"
              >
                <iconpark-icon name="arrow-refresh" size="20"></iconpark-icon>
              </span>
            </a-popover>
          </template>
          <template v-if="data.status === EXECUTION_STATUS.FINISHED">
            <a-button v-if="canEdit">
              <span @click="openWork">{{ $t('ji-xu-bian-ji') }}</span>
            </a-button>
            <a-popover>
              <template #content> {{ $t('xia-zai') }} </template>
              <span class="icon-action" @click="downVideo">
                <iconpark-icon name="download" size="18"></iconpark-icon>
              </span>
            </a-popover>
            <a-popover>
              <template #content> {{ $t('fen-xiang') }} </template>
              <TemplateShare
                class="share-style"
                :isCard="false"
                :title="`${$t('fen-xiang')}${$t('cheng-pian')}`"
                :showExtra="true"
                :params="{
                  share_ids: action ? [data[action.shareIdKey]] : [],
                  model_type: COLLECTION_MAP.PIECES,
                }"
                :linkUrl="getShareUrl(data)"
                :trackParams="{
                  ...from,
                  dataId: data.id,
                  dataTitle: data.title,
                }"
                :shareToOuterUser="shareToOuterUser"
              >
                <span class="icon-action">
                  <iconpark-icon
                    class="text-font-1"
                    name="arrow-share"
                  ></iconpark-icon>
                </span>
              </TemplateShare>
            </a-popover>
            <XCollect
              v-model="likes"
              class="collect-progress"
              :data="data"
              :size="16"
              :params="collectionParams"
              @reload="(ids: any) => paramsReload?.([ids])"
            />
          </template>
          <a-button
            v-if="
              allowAccelerate &&
              !data.is_speed &&
              [
                EXECUTION_STATUS.NOT_STARTED,
                EXECUTION_STATUS.DATA_INIT,
                EXECUTION_STATUS.WAITING,
              ].includes(data.status)
            "
            class="speed-btn"
            @click.stop="() => onAction(OPUS_ACTION_TYPE.SPEED)"
          >
            <iconpark-icon name="icon-light"></iconpark-icon> {{ $t('ti-su') }}
          </a-button>
          <a-button
            v-if="
              ![EXECUTION_STATUS.ERROR, EXECUTION_STATUS.FINISHED].includes(
                data.status
              )
            "
            class="min-w-[72px] font-medium"
            @click.stop="() => onAction(OPUS_ACTION_TYPE.CANCEL)"
          >
            {{ $t('qu-xiao-he-cheng') }}
          </a-button>
        </div>
        <!-- </div> -->
      </div>
      <div
        v-if="
          ![EXECUTION_STATUS.ERROR, EXECUTION_STATUS.FINISHED].includes(
            data.status
          )
        "
        class="-mb-[4px]"
      >
        <a-progress
          v-if="data?.xihe_percentage !== undefined"
          :percent="Math.round(data.xihe_percentage)"
          class="m-0 w-full"
          :stroke-width="4"
          :show-info="false"
          :strokeColor="data.is_speed ? '#FFB53D' : '#6962FF'"
        />
      </div>
      <div class="flex-y-center justify-between text-12 mt-[8px]">
        <div
          v-if="data.status === EXECUTION_STATUS.ERROR"
          class="text-12 text-error-6 flex-y-center gap-[4px]"
        >
          {{ $t('he-cheng-shi-bai') }}
          <a-popover v-if="data.show_error_reason" placement="topLeft">
            <template #content>
              {{ data.show_error_reason }}
            </template>
            <iconpark-icon
              name="icon-circle-info"
              size="16"
              class="text-font-80"
            ></iconpark-icon>
          </a-popover>
        </div>
        <div
          v-else-if="data.status !== EXECUTION_STATUS.FINISHED"
          class="text-font-60"
        >
          <span v-if="data.status === EXECUTION_STATUS.PROCESSING">
            {{ $t('he-cheng-zhong') }}
          </span>
          <span
            v-else-if="data.status === EXECUTION_STATUS.WAITING"
            :class="{
              'speed-done': data.is_speed,
            }"
          >
            {{
              $t('pai-dui-zhong-dang-qian-di-positioncount-wei', [
                positionCount,
              ])
            }}
          </span>
          <span v-else> {{ $t('ren-wu-chu-shi-hua-zhong') }} </span>
        </div>
        <div v-if="data.status === EXECUTION_STATUS.WAITING">
          {{
            $t('yu-ji-deng-dai-time', [
              getFormateTime(
                data.status === EXECUTION_STATUS.PROCESSING
                  ? data?.xihe_remaining_time
                  : data.xihe_queue_wait_time
              ),
            ])
          }}
        </div>
        <div v-if="data.status === EXECUTION_STATUS.PROCESSING">
          <span class="mr-[16px] text-font-60">
            {{
              $t('yu-ji-deng-dai-time', [
                getFormateTime(
                  data.status === EXECUTION_STATUS.PROCESSING
                    ? data?.xihe_remaining_time
                    : data.xihe_queue_wait_time
                ),
              ])
            }}
          </span>
          <span class="text-font-1 font-bold">
            {{ Math.round(data.xihe_percentage) }} %
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { downloadOssUrl } from '../oss-upload/service'
import { useTime } from '~/hooks/useTime'
import type { Template } from '~/api/interface'
import { getShareUrl } from '@/hooks/useModal/modals/share'
import { TemplateShare } from '@/features/template/components/templateShare'
import XCollect from '@/features/collect/index.vue'

const props = defineProps<{
  data: Template
  opAction: any
  action: any
  reload?: () => void
  cancel?: () => void
  allowAccelerate?: boolean
  allowCancelQueue?: boolean
  from: any
  shareToOuterUser?: boolean
  paramsReload?: (id: any) => void
  setLike?: (val: any) => void
  // collectionParams?: any
}>()

const collectionParams = computed(() => {
  const collectionParams: any = {
    is_user_upload: false,
    model_id: props.action?.collectModelIdKey
      ? props.data[props.action.collectModelIdKey]
      : undefined,
    model_type: COLLECTION_MAP.PIECES,
  }

  if (props.from?.source === SOURCE.MYSPACE) {
    collectionParams.source = COLLECTION_SOURCE.PERSON
  } else if (props.from?.source === SOURCE.TEAM_SPACE) {
    collectionParams.source = COLLECTION_SOURCE.TEAM
  } else if (props.from?.source === SOURCE.SHARE_WITH_ME) {
    collectionParams.source = COLLECTION_SOURCE.SHARE
  } else if (props.from?.source === SOURCE.COLLECT) {
    collectionParams.source = props.data.source
    collectionParams.resource_type = props.data.resource_type
  }
  return collectionParams
})

const likes = computed({
  get() {
    return props.data.like
  },
  set(value) {
    props.setLike?.(value)
  },
})

const { $t } = useNuxtApp()

// 可继续编辑
const canEdit = computed(() => {
  return (
    !props.data.project_is_delete &&
    (props.from?.source === SOURCE.MYSPACE ||
      (props.from?.source === SOURCE.COLLECT &&
        props.data.source === COLLECTION_SOURCE.PERSON))
  )
})

const normalBanners = ref([
  {
    image_url: getFeUrl(
      isChinese()
        ? '/youyan-platform/png/pieces/self.png'
        : '/youyan-platform/png/pieces/self-en.png'
    ),
  },
  {
    image_url: getFeUrl(
      isChinese()
        ? '/youyan-platform/png/pieces/stage.png'
        : '/youyan-platform/png/pieces/stage-en.png'
    ),
  },
  {
    image_url: getFeUrl(
      isChinese()
        ? '/youyan-platform/png/pieces/text.png'
        : '/youyan-platform/png/pieces/text-en.png'
    ),
  },
])

const { getFormateTime } = useTime()
const isVideo = computed(() => props.data.video_url || props.data.url?.[0])

const positionCount = computed(() => {
  if (
    props.data?.status === EXECUTION_STATUS.WAITING ||
    props.data?.status === EXECUTION_STATUS.DATA_INIT
  ) {
    return props.data.xihe_task_progress_info?.xihe_pending_task_count || 0
  }
  if (props.data?.status === EXECUTION_STATUS.NOT_STARTED) {
    return (
      props.data.xihe_task_progress_info?.not_started_pending_task_count || 0
    )
  }
})

function jumpEditor(data: any) {
  let queryStr = ''
  if (data?.generate_record_id) {
    queryStr = `?record_id=${data.generate_record_id}`
  }

  window.location.href = `/editor/${data?.project_id ?? data.id}/${
    data.secret_id
  }${queryStr}`
}

function openWork() {
  trackEvent(TRACK_EVENT.OPEN_OPUS, {
    target: props.from.target,
    source: props.from?.source,
    entrance: OPEN_ENTRANCE.MODEL,
    permission: 'edit',
  })
  jumpEditor(props.data)
}

const onAction = async (type: string) => {
  switch (type) {
    case OPUS_ACTION_TYPE.SPEED:
      await props.opAction?.speed?.()
      break
    case OPUS_ACTION_TYPE.CANCEL:
      await props.opAction?.cancel?.()
      break
    case OPUS_ACTION_TYPE.RETRY:
      await props.opAction?.retry?.()
      break
    // case OPUS_ACTION_TYPE.CANCEL_SPEED:
    //   await props.opAction?.cancelSpeed?.()
    //   break
    default:
      break
  }

  await props.reload?.()
}

// 下载视频
const downVideo = async () => {
  await downloadOssUrl(
    props.data.video_url || props.data.url?.[0],
    props.data.name || props.data.title
  )
}
</script>

<style lang="scss" scoped>
.opus-progress {
  border-right: 1px solid var(--back-3);
  /* Main Banner */
  .main-product {
    border-radius: 10px 0 0 0;
    overflow: hidden;
    position: relative;
    .banner {
      @apply h-[360px];
    }

    .custom-slick-arrow {
      z-index: 10;
      display: none !important;
      width: rem(32);
      height: rem(32);
      &.left {
        left: rem(10);
      }
      &.right {
        right: rem(10);
      }
      iconpark-icon {
        font-size: rem(32);
      }
    }

    &:hover {
      .custom-slick-arrow {
        display: flex !important;
      }
    }
  }
  .speed-btn {
    @apply text-[#7E490F] flex-y-center gap-[4px];
    border: none;
    background: linear-gradient(198.58deg, #ffe7a6 -11.35%, #fdc887 116.09%);
    &:hover {
      @apply text-[#7E490F];
      background: linear-gradient(
        198.58deg,
        rgba(255, 231, 166, 0.8) -11.35%,
        rgba(253, 200, 135, 0.8) 116.09%
      );
    }
  }
  .icon-action {
    @apply w-[24px] h-[24px] rounded-[4px] flex-y-center justify-center text-font-1 cursor-pointer;
    &:hover {
      @apply bg-back-2;
    }
  }

  :deep(.share-style) {
    color: var(--text-1);
    padding: 0;
    box-shadow: none;

    &:hover {
      color: var(--primary-color);
      background: none;
      box-shadow: none;
    }
  }
  :deep(.collect-progress) {
    // .like-btn {
    @apply w-[26px] h-[26px] rounded-[4px] flex-y-center justify-center text-font-1 cursor-pointer;
    background: none;
    .star {
      @apply text-font-1;
      font-size: 16px !important;
    }
    &:hover {
      @apply bg-back-2;
    }
    // }
  }
  .speed-done {
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background: linear-gradient(90deg, #c56d02 0%, #ebaf0f 100%);
    -webkit-background-clip: text;
  }
}
</style>
